<div class="relative flex flex-col gap-4 px-2 py-4">

  <!-- Success Notification -->
  @if (showToast()) {
    <app-successful-notification type="success" [title]="toastMessage()" />
  }

  <!-- Page Header -->
  <div class="flex flex-col gap-1">
    <h2 class="textPrimaryColor text-2xl font-bold" i18n="@@editReasonPageTitle">
      Edit Undelivered Reason
    </h2>
  </div>

  <!-- Loading State -->
  @if (loading()) {
    <div class="bgWhiteColor flex items-center justify-center rounded-2xl border border-gray-100 py-16 shadow-sm">
      <app-loading />
    </div>
  }

  <!-- Error State -->
  @else if (errorLoadingReason()) {
    <div class="bgWhiteColor flex flex-col items-center justify-center gap-4 rounded-2xl border border-gray-100 p-8 shadow-sm">
      <app-error [apiError]="errorLoadingReason()!" />
      <app-submit-button type="secondary" htmlType="button" (clicked)="onCancel()">
        <span i18n="@@backButton">{{ isArabic() ? 'العودة' : 'Back' }}</span>
      </app-submit-button>
    </div>
  }

  <!-- Form Card -->
  @else {
    <div class="bgWhiteColor rounded-2xl border border-gray-100 p-8 shadow-sm">
      <form (ngSubmit)="onSubmit()" novalidate class="flex flex-col gap-8">

        <!-- Inputs Row (2 Columns) -->
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">

          <!-- Arabic Reason Title Field -->
          <div class="flex flex-col gap-2">
            <label for="reasonAr" class="text-sm font-bold text-gray-800">
              <span i18n="@@reasonTitleAr">Reason Title (Arabic)</span>
              <span class="textPrimaryColor mx-1">*</span>
            </label>

            <input
              type="text"
              id="reasonAr"
              name="reasonAr"
              [ngModel]="reasonAr()"
              (ngModelChange)="reasonAr.set($event)"
              (blur)="markArTouched()"
              placeholder="Enter name in Arabic"
              i18n-placeholder="@@placeholderReasonAr"
              class="bgWhiteColor w-full rounded-lg border px-4 py-3 text-sm text-gray-700 outline-none transition-colors placeholder:text-gray-300 focus:border-black"
              [ngClass]="{
                'border-red-500 focus:border-red-500': isReasonArInvalid(),
                'border-gray-300 hover:border-gray-400': !isReasonArInvalid()
              }" />

            <!-- Validation Errors -->
            @if (isReasonArInvalid()) {
              <div class="flex flex-col gap-1 text-xs text-red-500">
                @if (reasonArErrors().required) {
                  <span i18n="@@errorReasonArRequired">Reason Name in Arabic is required</span>
                }
                @if (reasonArErrors().minlength) {
                  <span i18n="@@errorReasonArMinLength">Reason Name must be at least 3 characters</span>
                }
                @if (reasonArErrors().maxlength) {
                  <span i18n="@@errorReasonArMaxLength">Reason Name cannot exceed 255 characters</span>
                }
                @if (reasonArErrors().hasNumbers) {
                  <span i18n="@@errorReasonArNoNumbers">Reason Name cannot contain numbers</span>
                }
                @if (reasonArErrors().hasSpacePadding) {
                  <span i18n="@@errorReasonArSpacePadding">Arabic Reason title cannot start or end with spaces</span>
                }
              </div>
            }
          </div>

          <!-- English Reason Title Field -->
          <div class="flex flex-col gap-2">
            <label for="reasonEn" class="text-sm font-bold text-gray-800">
              <span i18n="@@reasonTitleEn">Reason Title (English)</span>
            </label>

            <input
              type="text"
              id="reasonEn"
              name="reasonEn"
              [ngModel]="reasonEn()"
              (ngModelChange)="reasonEn.set($event)"
              (blur)="markEnTouched()"
              placeholder="Enter name in English (optional)"
              i18n-placeholder="@@placeholderReasonEn"
              class="bgWhiteColor w-full rounded-lg border px-4 py-3 text-sm text-gray-700 outline-none transition-colors placeholder:text-gray-300 focus:border-black"
              [ngClass]="{
                'border-red-500 focus:border-red-500': isReasonEnInvalid(),
                'border-gray-300 hover:border-gray-400': !isReasonEnInvalid()
              }" />

            <!-- Validation Errors -->
            @if (isReasonEnInvalid()) {
              <div class="flex flex-col gap-1 text-xs text-red-500">
                @if (reasonEnErrors().minlength) {
                  <span i18n="@@errorReasonEnMinLength">Reason Name must be at least 3 characters</span>
                }
                @if (reasonEnErrors().maxlength) {
                  <span i18n="@@errorReasonEnMaxLength">Reason Name cannot exceed 255 characters</span>
                }
                @if (reasonEnErrors().hasNumbers) {
                  <span i18n="@@errorReasonEnNoNumbers">Reason Name cannot contain numbers</span>
                }
                @if (reasonEnErrors().hasSpacePadding) {
                  <span i18n="@@errorReasonEnSpacePadding">English Reason title cannot start or end with spaces</span>
                }
              </div>
            }
          </div>

        </div>

        <!-- API Error Display -->
        @if (apiError()) {
          <app-error [apiError]="apiError()" />
        }

        <!-- Actions Buttons Row -->
        <div class="flex items-center justify-end gap-4 pt-2">
          <app-submit-button
            type="secondary"
            htmlType="button"
            (clicked)="onCancel()">
            <span i18n="@@cancelButton">Cancel</span>
          </app-submit-button>

          <app-submit-button
            type="primary"
            htmlType="submit"
            [disabled]="isSubmitting()">
            @if (isSubmitting()) {
              <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
            } @else {
              <span i18n="@@submitButton">{{ isArabic() ? 'إرسال' : 'Submit' }}</span>
            }
          </app-submit-button>
        </div>

      </form>
    </div>
  }

  <!-- Confirmation Dialog -->
  @if (showConfirmModal()) {
    <app-confirmation-dialog
      [layout]="'start'"
      [title]="isArabic() ? 'تأكيد التعديلات' : 'Confirm Changes'"
      [description]="isArabic() ? 'هل أنت متأكد من حفظ التغييرات لهذه البيانات؟' : 'Are you sure you want to save changes for this reason?'"
      [confirmContent]="isArabic() ? 'تأكيد' : 'Confirm'"
      [cancelContent]="isArabic() ? 'إلغاء' : 'Cancel'"
      [isLoading]="isSubmitting()"
      [error]="apiError()"
      (onSubmit)="confirmSubmit()"
      (onCancel)="closeConfirmModal()" />
  }

</div>